import { put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import config from '../../../config';
import {
  REQUEST,
  fetchSuccess,
  fetchFailure
} from '../../actions/forecasts/predictions';
import { getLocalUser } from '../../../utils/localUser';
import { Error as ErrorAction } from '../../actions/error/error';
import { extractErrorMessage } from '../../../utils/apiResponseHandler';

// Helper function to validate date strings
const isValidDate = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

// Clean chart data to ensure valid dates
const cleanChartData = (chartData) => {
  if (!chartData || !Array.isArray(chartData)) return [];
  
  return chartData.map(series => {
    if (!series.data || !Array.isArray(series.data)) {
      return { ...series, data: [] };
    }
    
    // Filter out invalid data points
    const cleanedData = series.data
      .filter(point => {
        if (!point || typeof point !== 'object') return false;
        if (point.y === undefined || point.y === null) return false;
        
        // Check if x is a valid date
        try {
          if (!point.x) return false;
          const date = new Date(point.x);
          return !isNaN(date.getTime());
        } catch (e) {
          console.warn('Invalid date in data point:', point.x);
          return false;
        }
      });
    
    return { ...series, data: cleanedData };
  });
};

export function* fetchPredictionsRequest(action) {
  try {
    const { segmentId, startDate, endDate, limit = 1000 } = action.payload || {};
    
    if (!segmentId) {
      throw new Error('Segment ID is required to fetch predictions');
    }
    
    // Make sure limit is a valid integer
    let limitValue;
    try {
      limitValue = Number(limit);
      if (isNaN(limitValue) || !Number.isInteger(limitValue)) {
        limitValue = 1000; // Default fallback if limit is not a valid integer
        console.warn('Invalid limit value provided, using default of 1000');
      }
    } catch (e) {
      limitValue = 1000; // Default fallback
      console.warn('Error processing limit value, using default of 1000', e);
    }
    
    const params = {
      segment_id: segmentId,
      limit: limitValue
    };
    
    // Add optional parameters if they are provided
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    // Get headers using the utility function
    const headers = getLocalUser();
    
    if (!headers.Authorization) {
      throw new Error('Authentication token not found');
    }
    
    // Make the API call
    const response = yield axios.get(`${config.API_URL}/forecast/get_predictions_chart`, {
      params,
      headers
    });
    
    // Extract and clean the data
    const data = response.data.data || {};
    
    // Clean the chartData to ensure valid dates
    const cleanedChartData = cleanChartData(data.chartData);

    // Include the segmentId in the response to ensure we can map data correctly
    yield put(fetchSuccess({
      ...data,
      chartData: cleanedChartData,
      segmentId // Include the original segmentId
    }));
    
  } catch (error) {
    // Log detailed error information
    console.error('Error in fetchPredictionsRequest:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      requestParams: action.payload
    });
    
    // If there's an error, dispatch the error action
    yield put(ErrorAction(error));

    yield put(fetchFailure(extractErrorMessage(error)));
  }
}

export function* watch() {
  yield takeLatest(REQUEST, fetchPredictionsRequest);
} 